import React, { useContext, useEffect, useMemo, useState } from "react";
import DraggableStepper from "../ui/draggable-stepper";
import { Check, Play } from "lucide-react";
import { WorkoutContext } from "../../contexts/WorkoutContext";
import { useAuth } from "../../contexts/AuthContext";
import { useUserProfile } from "../../contexts/UserProfileContext";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import DetailSheet from "../session/generate/DetailSheet";
import { cn } from "../../lib/utils";
import useUiMode from "../../hooks/useUiMode";
import { GOAL_CATEGORIES, TOP_GOALS } from "../../config/goals";
import { EQUIPMENT_CATEGORIES, EQUIPMENT_LOCATION_MAP } from "../../config/equipment";
import { computeWeeklyTargets } from "../../utils/weeklyTargets";
import {
  hasSeenSetup,
  markSetupSeen,
  onOpenSetup,
  requestStartToday,
} from "../../utils/setupFlow";

const STEPS = ["goal", "time", "kit", "you", "done"];
const LAST_LOCATION_KEY = "khronos_last_location";

const PLACES = [
  { value: "Home", label: "Home", hint: "Dumbbells, kettlebells, bands" },
  { value: "Gym", label: "Gym", hint: "Everything a full gym has" },
  { value: "Outdoor", label: "Outdoors", hint: "A park, a track, the street" },
  { value: "None", label: "No equipment", hint: "Bodyweight only" },
];

const LEVELS = [
  { value: "beginner", label: "New to it", hint: "Under 6 months of regular training" },
  { value: "intermediate", label: "Some experience", hint: "6 months to 3 years" },
  { value: "advanced", label: "Experienced", hint: "3 years or more" },
];

// Shown straight away under the location's own kit; the rest of the list is
// one tap further.
const COMMON_KIT_GROUPS = ["Basic", "Free Weights", "Cardio"];

const MIX_DOT = { resistance: "bg-red-400", anaerobic: "bg-amber-400", aerobic: "bg-sky-400" };
const MIX_LABEL = {
  resistance: ["strength", "strength"],
  anaerobic: ["hard interval", "hard intervals"],
  aerobic: ["cardio", "cardio"],
};

const sameKit = (a, b) => a.size === b.length && b.every((k) => a.has(k));

const draftFrom = (profile) => {
  const place = PLACES.some((p) => p.value === profile.trainingLocation)
    ? profile.trainingLocation
    : "Home";
  return {
    goal: profile.primaryGoal || "General Fitness",
    days: profile.weeklySessions > 0 ? profile.weeklySessions : 3,
    mins: profile.timeAvailable || profile.sessionDuration || 45,
    place,
    kit: new Set(EQUIPMENT_LOCATION_MAP[place] || []),
    level: LEVELS.some((l) => l.value === profile.fitnessLevel)
      ? profile.fitnessLevel
      : "intermediate",
    birth: profile.birthYear ? String(profile.birthYear) : "",
    injuries: profile.injuryNotes || "",
  };
};

const Choice = ({ selected, title, hint, onClick, dashed }) => (
  <button
    type="button"
    aria-pressed={selected}
    onClick={onClick}
    className={cn(
      "grid gap-0.5 rounded-2xl border p-3 text-left transition-colors",
      dashed && "border-dashed",
      selected ? "border-primary bg-primary/10" : "border-border bg-card hover:border-primary/40",
    )}
  >
    <span className={cn("text-[15px] font-semibold", selected ? "text-primary" : "text-white")}>
      {title}
    </span>
    {hint && <span className="text-xs leading-snug text-muted-foreground">{hint}</span>}
  </button>
);

const Chip = ({ selected, children, onClick }) => (
  <button
    type="button"
    aria-pressed={selected}
    onClick={onClick}
    className={cn(
      "rounded-full border px-3 py-2 text-[13px] transition-colors",
      selected
        ? "border-primary bg-primary/10 text-primary"
        : "border-border bg-card text-foreground hover:border-primary/40",
    )}
  >
    {children}
  </button>
);

/**
 * The first-launch walkthrough: goal, time, kit, and a little about you, each
 * showing what it changes. Opens by itself once for someone new, and any time
 * from the Session tab's banner or Profile. Skip keeps what's been chosen.
 */
const GettingStarted = () => {
  const { userProfile, setUserProfile, setView } = useContext(WorkoutContext);
  const { isAuthenticated, loading: authLoading } = useAuth();
  const { sessions, hasLoadedUserData, addEquipmentPreset } = useUserProfile();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const { advanced, setAdvanced } = useUiMode();
  const [draft, setDraft] = useState(() => draftFrom(userProfile));
  // Only what was actually chosen is saved, so skipping straight past a step
  // never pins its defaults (days stays on Auto unless picked).
  const [touched, setTouched] = useState(() => new Set());
  const [goalSheet, setGoalSheet] = useState(false);
  const [allKit, setAllKit] = useState(false);

  const begin = () => {
    setDraft(draftFrom(userProfile));
    setTouched(new Set());
    setStep(0);
    setAllKit(false);
    setOpen(true);
  };

  useEffect(() => onOpenSetup(begin));

  // Offer itself once to someone new: no history, nothing set up yet. Waits
  // for sign-in and the account's data so an existing user never sees it.
  useEffect(() => {
    if (open || authLoading || hasSeenSetup()) return;
    if (isAuthenticated && !hasLoadedUserData) return;
    let localHistory = 0;
    try {
      localHistory = JSON.parse(localStorage.getItem("completedSessions") || "[]").length;
    } catch {
      localHistory = 0;
    }
    const isNew =
      (sessions?.length || 0) === 0 &&
      localHistory === 0 &&
      !userProfile.weeklySessions &&
      !userProfile.birthYear;
    if (isNew) begin();
    else markSetupSeen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, isAuthenticated, hasLoadedUserData]);

  const set = (key, value) => {
    setDraft((d) => ({ ...d, [key]: value }));
    setTouched((t) => new Set(t).add(key));
  };

  const preview = useMemo(
    () =>
      computeWeeklyTargets({
        userProfile: { ...userProfile, primaryGoal: draft.goal, weeklySessions: draft.days },
      }),
    [userProfile, draft.goal, draft.days],
  );

  const save = () => {
    const next = { ...userProfile };
    if (touched.has("goal")) next.primaryGoal = draft.goal;
    if (touched.has("days")) next.weeklySessions = draft.days;
    if (touched.has("mins")) {
      next.timeAvailable = draft.mins;
      next.sessionDuration = draft.mins;
    }
    if (touched.has("place") || touched.has("kit")) {
      next.trainingLocation = draft.place;
      next.availableEquipment = [...draft.kit];
      try {
        localStorage.setItem(LAST_LOCATION_KEY, draft.place);
      } catch {
        /* the generate screen falls back to the profile location */
      }
      // A kit that differs from the place's usual set becomes the default
      // equipment preset, which the generate screen uses ahead of the place.
      const usual = EQUIPMENT_LOCATION_MAP[draft.place] || [];
      if (isAuthenticated && !sameKit(draft.kit, usual)) {
        addEquipmentPreset?.({
          name: "My kit",
          equipment: [...draft.kit],
          is_default: true,
        }).catch(() => {});
      }
    }
    if (touched.has("level")) {
      next.fitnessLevel = draft.level;
      next.trainingExperience = draft.level;
    }
    const year = Number(draft.birth);
    if (touched.has("birth") && year >= 1900 && year <= new Date().getFullYear()) {
      next.birthYear = year;
    }
    if (touched.has("injuries")) next.injuryNotes = draft.injuries.trim();
    if (touched.size > 0) setUserProfile(next);
    markSetupSeen();
  };

  const finish = (startNow) => {
    save();
    setOpen(false);
    if (startNow) requestStartToday();
    setView?.("session");
  };

  if (!open) return null;

  const name = STEPS[step];
  const age = Number(draft.birth) > 1900 ? new Date().getFullYear() - Number(draft.birth) : null;
  const isTopGoal = TOP_GOALS.some(([g]) => g === draft.goal);
  const placeKit = EQUIPMENT_LOCATION_MAP[draft.place] || [];

  const mix = (note) => (
    <div className="grid gap-2 rounded-2xl border border-primary/40 bg-primary/[0.07] p-3" aria-live="polite">
      <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-primary">
        {name === "goal" ? "Your week would look like" : "Each week"}
      </p>
      <div className="flex flex-wrap gap-1.5">
        {Object.entries(preview.counts).map(([tag, n]) =>
          n > 0 ? (
            <span
              key={tag}
              className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-2.5 py-1 text-[13px]"
            >
              <span className={cn("h-2 w-2 rounded-full", MIX_DOT[tag])} />
              {n} {MIX_LABEL[tag][n === 1 ? 0 : 1]}
            </span>
          ) : null,
        )}
        <span className="rounded-full border border-border bg-card px-2.5 py-1 text-[13px]">
          {draft.mins} min each
        </span>
      </div>
      <p className="text-[13px] leading-relaxed text-muted-foreground">
        {note ||
          `${preview.familyLabel} goal, ${draft.days} ${draft.days === 1 ? "day" : "days"} a week. What's next and your Activity page aim for this mix.`}
      </p>
    </div>
  );

  let body;
  let footer;
  if (name === "goal") {
    body = (
      <>
        <div>
          <h2 className="text-[26px] font-bold leading-tight text-white [text-wrap:balance]">
            What are you training for?
          </h2>
          <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
            This sets the balance of your week: how much lifting, how many hard
            intervals, how much cardio. You can change it any time.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {TOP_GOALS.map(([goal, hint]) => (
            <Choice
              key={goal}
              selected={draft.goal === goal}
              title={goal}
              hint={hint}
              onClick={() => set("goal", goal)}
            />
          ))}
          <Choice
            dashed
            selected={!isTopGoal}
            title={isTopGoal ? "Something else" : draft.goal}
            hint={isTopGoal ? "Endurance, skills, longevity…" : "Tap to change"}
            onClick={() => setGoalSheet(true)}
          />
        </div>
        {mix()}
      </>
    );
  } else if (name === "time") {
    body = (
      <>
        <div>
          <h2 className="text-[26px] font-bold leading-tight text-white [text-wrap:balance]">
            How much time do you have?
          </h2>
          <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
            Be realistic. A plan you can keep beats an ambitious one you can't.
          </p>
        </div>
        <div>
          <p className="mb-2 text-xs font-semibold text-muted-foreground">Days a week</p>
          <div className="grid grid-cols-7 gap-1.5">
            {[1, 2, 3, 4, 5, 6, 7].map((n) => (
              <button
                key={n}
                type="button"
                aria-pressed={draft.days === n}
                onClick={() => set("days", n)}
                className={cn(
                  "h-12 rounded-xl border text-base font-bold tabular-nums transition-colors",
                  draft.days === n
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-border bg-card text-white hover:border-primary/40",
                )}
              >
                {n}
              </button>
            ))}
          </div>
        </div>
        <div>
          <p className="mb-2 flex justify-between text-xs font-semibold text-muted-foreground">
            Each session
            <span className="font-normal text-muted-foreground/70">drag sideways, or tap + and −</span>
          </p>
          <DraggableStepper
            prominent
            momentum
            unit="min"
            ariaLabel="Time available"
            id="setup-minutes"
            value={draft.mins}
            onChange={(v) => set("mins", v)}
            min={10}
            max={120}
            step={5}
          />
        </div>
        {mix(
          draft.mins <= 30
            ? "Short sessions use supersets and quick transitions to fit the work in."
            : null,
        )}
      </>
    );
  } else if (name === "kit") {
    const shown = new Set([...placeKit, ...COMMON_KIT_GROUPS.flatMap((g) => EQUIPMENT_CATEGORIES[g] || [])]);
    body = (
      <>
        <div>
          <h2 className="text-[26px] font-bold leading-tight text-white [text-wrap:balance]">
            Where do you usually train?
          </h2>
          <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
            Khronos only plans exercises you can do with what you have. You can
            add other places later.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {PLACES.map((p) => (
            <Choice
              key={p.value}
              selected={draft.place === p.value}
              title={p.label}
              hint={p.hint}
              onClick={() => {
                set("place", p.value);
                setDraft((d) => ({ ...d, kit: new Set(EQUIPMENT_LOCATION_MAP[p.value] || []) }));
              }}
            />
          ))}
        </div>
        <div className="grid gap-2">
          <p className="flex justify-between text-xs font-semibold text-muted-foreground">
            What's there
            <span className="font-normal text-muted-foreground/70">tap to add or remove</span>
          </p>
          {(allKit
            ? Object.entries(EQUIPMENT_CATEGORIES)
            : [["", [...shown]]]
          ).map(([group, items]) => (
            <div key={group || "common"} className="grid gap-1.5">
              {group && (
                <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                  {group}
                </p>
              )}
              <div className="flex flex-wrap gap-1.5">
                {items.map((item) => (
                  <Chip
                    key={item}
                    selected={draft.kit.has(item)}
                    onClick={() => {
                      const kit = new Set(draft.kit);
                      if (kit.has(item)) kit.delete(item);
                      else kit.add(item);
                      set("kit", kit);
                    }}
                  >
                    {item}
                  </Chip>
                ))}
              </div>
            </div>
          ))}
          {!allKit && (
            <button
              type="button"
              onClick={() => setAllKit(true)}
              className="justify-self-start text-sm font-medium text-primary hover:underline"
            >
              Show all equipment
            </button>
          )}
        </div>
      </>
    );
  } else if (name === "you") {
    body = (
      <>
        <div>
          <h2 className="text-[26px] font-bold leading-tight text-white [text-wrap:balance]">
            A little about you
          </h2>
          <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
            These help Khronos set the right loads and recovery. All optional.
          </p>
        </div>
        <div className="grid gap-2">
          <p className="text-xs font-semibold text-muted-foreground">Training experience</p>
          {LEVELS.map((l) => (
            <Choice
              key={l.value}
              selected={draft.level === l.value}
              title={l.label}
              hint={l.hint}
              onClick={() => set("level", l.value)}
            />
          ))}
        </div>
        <div className="grid gap-1.5">
          <label htmlFor="setup-birth" className="text-sm font-semibold text-white">
            Year of birth <span className="text-xs font-normal text-muted-foreground">optional</span>
          </label>
          <Input
            id="setup-birth"
            inputMode="numeric"
            maxLength={4}
            placeholder="e.g. 1985"
            value={draft.birth}
            onChange={(e) => set("birth", e.target.value.replace(/\D/g, ""))}
            className="h-11 bg-card text-base"
          />
          <p className="text-xs leading-snug text-muted-foreground">
            {age != null && age >= 65
              ? "From 65, Khronos adds balance and power work and will suggest strength on 3 or more days a week."
              : "Used to adjust recovery and, from 65, to add balance and power work."}
          </p>
        </div>
        <div className="grid gap-1.5">
          <label htmlFor="setup-injuries" className="text-sm font-semibold text-white">
            Injuries or limits{" "}
            <span className="text-xs font-normal text-muted-foreground">optional</span>
          </label>
          <Textarea
            id="setup-injuries"
            rows={2}
            placeholder="e.g. Sore left knee, no overhead pressing"
            value={draft.injuries}
            onChange={(e) => set("injuries", e.target.value)}
            className="resize-none bg-card text-base"
          />
          <p className="text-xs text-muted-foreground">
            Sessions avoid or adapt anything you list here.
          </p>
        </div>
      </>
    );
  } else {
    const placeLabel = PLACES.find((p) => p.value === draft.place)?.label;
    const rows = [
      ["Goal", draft.goal, 0],
      [
        "Each week",
        Object.entries(preview.counts)
          .filter(([, n]) => n > 0)
          .map(([tag, n]) => `${n} ${MIX_LABEL[tag][n === 1 ? 0 : 1]}`)
          .join(", "),
        1,
      ],
      ["Sessions", `${draft.days} days · ${draft.mins} min`, 1],
      ["Training at", `${placeLabel} · ${draft.kit.size} items`, 2],
    ];
    body = (
      <>
        <span className="grid h-14 w-14 place-items-center rounded-full bg-secondary/15 text-secondary">
          <Check className="h-7 w-7" strokeWidth={3} />
        </span>
        <div>
          <h2 className="text-[26px] font-bold leading-tight text-white">You're set</h2>
          <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
            Here's what Khronos will plan around. It's all in your Profile if you
            want to change it later.
          </p>
        </div>
        <div className="divide-y divide-border rounded-2xl border border-border bg-card">
          {rows.map(([label, value, go]) => (
            <div
              key={label}
              className="grid grid-cols-[6.5rem_minmax(0,1fr)_auto] items-baseline gap-2 px-3.5 py-3"
            >
              <span className="text-[13px] text-muted-foreground">{label}</span>
              <span className="text-sm font-semibold text-white">{value}</span>
              <button
                type="button"
                onClick={() => setStep(go)}
                className="text-[13px] text-primary hover:underline"
              >
                Change
              </button>
            </div>
          ))}
        </div>
        {/* Basic / Advanced (#172): the place a new person chooses. Also in
            the menu under the profile picture. */}
        <div className="grid gap-2">
          <span className="text-[13px] text-muted-foreground">Options shown</span>
          <div className="grid grid-cols-2 gap-2" role="group" aria-label="Options shown">
            {[
              [false, "Keep it simple"],
              [true, "Show me everything"],
            ].map(([value, label]) => (
              <button
                key={label}
                type="button"
                aria-pressed={advanced === value}
                onClick={() => setAdvanced(value)}
                className={cn(
                  "rounded-xl border px-3 py-2.5 text-sm font-semibold transition-colors",
                  advanced === value
                    ? "border-primary/50 bg-primary/10 text-primary"
                    : "border-border bg-card text-white hover:border-primary/40",
                )}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </>
    );
  }

  if (name === "done") {
    footer = (
      <>
        <Button className="h-12 w-full text-base font-bold" onClick={() => finish(true)}>
          <Play className="mr-2 h-4 w-4 fill-current" />
          Build my first session
        </Button>
        <Button
          variant="outline"
          className="h-12 w-full border-border bg-muted/40 text-base font-semibold hover:bg-muted/60"
          onClick={() => finish(false)}
        >
          I'll look around first
        </Button>
      </>
    );
  } else {
    footer = (
      <Button className="h-12 w-full text-base font-bold" onClick={() => setStep((s) => s + 1)}>
        {name === "you" ? "Finish" : "Next"}
      </Button>
    );
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Getting started"
      className="fixed inset-0 z-[55] flex flex-col bg-background pt-[env(safe-area-inset-top)]"
    >
      <div className="mx-auto grid w-full max-w-xl grid-cols-[1fr_auto_1fr] items-center px-4 py-3">
        <button
          type="button"
          onClick={() => setStep((s) => Math.max(0, s - 1))}
          className={cn(
            "justify-self-start px-1 py-1.5 text-sm text-muted-foreground hover:text-foreground",
            (step === 0 || name === "done") && "invisible",
          )}
        >
          Back
        </button>
        <div className="flex gap-1.5" aria-label={`Step ${Math.min(step + 1, 4)} of 4`}>
          {STEPS.slice(0, 4).map((s, i) => (
            <span
              key={s}
              className={cn(
                "h-1 w-6 rounded-full",
                i === step ? "bg-primary" : i < step ? "bg-primary/45" : "bg-white/10",
              )}
            />
          ))}
        </div>
        <button
          type="button"
          onClick={() => setStep(STEPS.length - 1)}
          className={cn(
            "justify-self-end px-1 py-1.5 text-sm text-muted-foreground hover:text-foreground",
            name === "done" && "invisible",
          )}
        >
          Skip
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto grid w-full max-w-xl gap-5 px-4 pb-6 pt-2">{body}</div>
      </div>

      <div className="border-t border-border pb-[env(safe-area-inset-bottom)]">
        <div className="mx-auto grid w-full max-w-xl gap-2 px-4 py-3">{footer}</div>
      </div>

      <DetailSheet
        open={goalSheet}
        onOpenChange={setGoalSheet}
        title="Other goals"
        description="Pick what this training is for"
      >
        {Object.entries(GOAL_CATEGORIES).map(([category, goals]) => (
          <div key={category} className="space-y-2">
            <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
              {category}
            </p>
            <div className="flex flex-wrap gap-1.5">
              {goals.map((goal) => (
                <Chip key={goal} selected={draft.goal === goal} onClick={() => set("goal", goal)}>
                  {goal}
                </Chip>
              ))}
            </div>
          </div>
        ))}
      </DetailSheet>
    </div>
  );
};

export default GettingStarted;
